import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  Save,
  Trash2,
  Search,
  Edit2,
  Plus,
  Info,
  Link,
  Loader2,
  RefreshCw,
  Download,
  CheckCircle2,
  Wrench,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchTrades } from "@/api/fetchTrades/fetchTradesApi";
import type { Trade } from "@/api/fetchTrades/types";
import {
  getQuickBooksTrade,
  createQuickBooksTrade,
} from "@/api/quickBooksTrade/quickBooksTradeApi";
import type { QuickBooksTradeResponse } from "@/api/quickBooksTrade/types";
import { fetchIntegrationsStatusAction } from "@/modules/hub/features/UserAppSettings/slices/integrationsStatusSlice";
import {
  deleteMembershipSku,
  fetchMembershipSkus,
  selectMembershipSkuLoading,
  selectMembershipSkus,
  setCurrentSku,
  updateMembershipSku,
} from "@/modules/hub/features/MembershipSkuManagement/slice/membershipSkuSlice";
import type { MembershipSku } from "@/modules/hub/features/MembershipSkuManagement/schemas/membershipSkuSchema";
import { EditMembershipSkuDrawer } from "@/modules/hub/features/MembershipSkuManagement/components/EditMembershipSkuDrawer/EditMembershipSkuDrawer";
import { DeleteConfirmDialog } from "@/modules/hub/features/MembershipSkuManagement/components/DeleteConfirmDialog/DeleteConfirmDialog";
import { createMembershipSku } from "@/api/createMembershipSku/createMembershipSkuApi";
import { fetchCompanyProfileAction } from "@/modules/hub/features/UserAppSettings/slices/companyProfileSlice";
import { useNotification } from "@/context/NotificationContext";

interface MembershipSkuSectionProps {
  companyUuid: string;
  currentTenant?: string;
}

export const MembershipSkuSection = ({
  companyUuid,
  currentTenant,
}: MembershipSkuSectionProps) => {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();
  const skus = useAppSelector(selectMembershipSkus);
  const loading = useAppSelector(selectMembershipSkuLoading);

  const [searchTerm, setSearchTerm] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [skuToDelete, setSkuToDelete] = useState<MembershipSku | null>(null);
  const [localSkus, setLocalSkus] = useState<MembershipSku[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Trade selector state
  const [trades, setTrades] = useState<Trade[]>([]);
  const [quickBooksTrade, setQuickBooksTrade] =
    useState<QuickBooksTradeResponse | null>(null);
  const [selectedTradeId, setSelectedTradeId] = useState<string>("");
  const [isLoadingTrades, setIsLoadingTrades] = useState(true);
  const [isSavingTrade, setIsSavingTrade] = useState(false);
  const [tradeError, setTradeError] = useState("");

  // Add a new SKU form state
  const [newSkuPattern, setNewSkuPattern] = useState("");
  const [membershipType, setMembershipType] = useState<"annual" | "monthly">(
    "annual",
  );
  const [description, setDescription] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const loadTradeData = async () => {
      setIsLoadingTrades(true);
      try {
        const [tradesResponse, currentTrade] = await Promise.all([
          fetchTrades(),
          getQuickBooksTrade(companyUuid),
        ]);
        setTrades(
          [...tradesResponse.data].sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        );
        setQuickBooksTrade(currentTrade);
      } catch (error) {
        console.error("Failed to load trade data:", error);
        showNotification("Error", "Failed to load trade data.", "error");
      } finally {
        setIsLoadingTrades(false);
      }
    };
    loadTradeData();
  }, [companyUuid]);

  const handleSaveTrade = async () => {
    if (!selectedTradeId) {
      setTradeError("Please select a trade");
      return;
    }
    setTradeError("");
    setIsSavingTrade(true);
    try {
      const result = await createQuickBooksTrade(companyUuid, {
        tradeId: Number(selectedTradeId),
      });
      setQuickBooksTrade(result);
      setSelectedTradeId("");
      dispatch(fetchIntegrationsStatusAction(false));
    } catch (error) {
      console.error("Failed to save trade:", error);
      setTradeError("Failed to save trade. It may already be configured.");
    } finally {
      setIsSavingTrade(false);
    }
  };

  useEffect(() => {
    console.log(
      "[MembershipSkuSection] Fetching SKUs for tenant:",
      currentTenant,
    );
    dispatch(fetchMembershipSkus(currentTenant));
  }, [dispatch, currentTenant]);

  useEffect(() => {
    const skuArray = Array.isArray(skus) ? skus : [];
    setLocalSkus(skuArray);
    setHasChanges(false); // Reset changes when data is reloaded
    console.log("[MembershipSkuSection] SKUs from Redux:", skuArray);
    console.log("[MembershipSkuSection] Loading:", loading);
  }, [skus, loading]);

  const handleToggleStatus = (uuid: string) => {
    setLocalSkus((prev) =>
      prev.map((sku) =>
        sku.uuid === uuid ? { ...sku, isActive: !sku.isActive } : sku,
      ),
    );
    setHasChanges(true);
  };

  const handleEdit = (sku: MembershipSku) => {
    dispatch(setCurrentSku(sku));
    setIsEditOpen(true);
  };

  const handleDelete = (sku: MembershipSku) => {
    setSkuToDelete(sku);
  };

  const confirmDelete = async () => {
    if (skuToDelete) {
      await dispatch(deleteMembershipSku(skuToDelete.uuid));
      dispatch(fetchCompanyProfileAction(false));
      setSkuToDelete(null);
    }
  };

  const handleSaveAllChanges = async () => {
    setIsSaving(true);
    try {
      // Update each SKU that has been modified
      for (const sku of localSkus) {
        const originalSku = skus.find((s) => s.uuid === sku.uuid);
        if (originalSku && originalSku.isActive !== sku.isActive) {
          await dispatch(
            updateMembershipSku({
              uuid: sku.uuid,
              data: {
                skuPattern: sku.skuPattern,
                membershipDuration: sku.membershipDuration,
                description: sku.description,
                isActive: sku.isActive,
              },
            }),
          ).unwrap();
        }
      }
      setHasChanges(false);
      // Refresh the list and update the integration status
      dispatch(fetchMembershipSkus(currentTenant));
      dispatch(fetchCompanyProfileAction(false));
    } catch (error) {
      console.error("Failed to save changes:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSku = async () => {
    setValidationError("");

    if (!newSkuPattern.trim()) {
      setValidationError("SKU code is required");
      return;
    }

    const skuArray = Array.isArray(skus) ? skus : [];
    if (
      skuArray.some(
        (s) => s.skuPattern?.toLowerCase() === newSkuPattern.toLowerCase(),
      )
    ) {
      setValidationError("This SKU already exists");
      return;
    }

    if (!currentTenant) {
      setValidationError("Tenant information is missing");
      return;
    }

    try {
      setIsCreating(true);
      await createMembershipSku({
        tenant: currentTenant,
        skuPattern: newSkuPattern,
        membershipDuration: membershipType,
        description: description || undefined,
      });

      // Refresh the list and update the integration status
      dispatch(fetchMembershipSkus(currentTenant));
      dispatch(fetchCompanyProfileAction(false));
      setNewSkuPattern("");
      setMembershipType("annual");
      setDescription("");
    } catch (error) {
      console.error("Failed to create membership SKU:", error);
      setValidationError("Failed to create SKU. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const filteredSkus = localSkus.filter(
    (sku) =>
      sku.skuPattern?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sku.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getValidityPeriod = (duration: string) => {
    return duration === "annual" ? "12 months" : "30 days";
  };

  const getMembershipTypeLabel = (duration: string) => {
    return duration === "annual" ? "Annual" : "Monthly";
  };

  const activeCount = localSkus.filter((sku) => sku.isActive).length;
  const totalCount = localSkus.length;

  const isInitialLoading = isLoadingTrades || loading;

  if (isInitialLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
        <Loader2 className="h-8 w-8 animate-spin mb-3" />
        <p className="text-sm">Loading QBO integration settings...</p>
      </div>
    );
  }

  const steps = [
    {
      number: 1,
      icon: Link,
      title: "Connect to QuickBooks Online",
      description:
        "Link your QuickBooks account using the Data Connector page. This establishes a secure OAuth connection to access your data.",
      action: "Go to Data Connector",
      actionHref: "/hub/data-connector",
    },
    {
      number: 2,
      icon: RefreshCw,
      title: "Run Job & Wait for Sync to Complete",
      description:
        'On the Data Connector page, click "Run Job" to sync your data with QuickBooks. Wait until the sync completes before proceeding.',
    },
    {
      number: 3,
      icon: Wrench,
      title: "Configure Trade",
      description:
        "Select the trade for your QuickBooks Online integration. This determines how invoices are categorized during import.",
    },
    {
      number: 4,
      icon: Plus,
      title: "Configure Membership SKUs",
      description:
        "Define the SKU codes that identify club memberships in your QuickBooks invoices. These are used to automatically detect and tag club members.",
    },
    {
      number: 5,
      icon: Download,
      title: "Request QBO Data Import",
      description:
        "Contact the support team to initiate a QuickBooks Online data import into your account.",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-1">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Getting Started
          </h2>
          <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded">
            SETUP GUIDE
          </span>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Follow these steps to import your QuickBooks customer and invoice data
          into Hub+.
        </p>

        <div className="relative">
          {steps.map((step, index) => (
            <div className="flex gap-4 relative" key={step.number}>
              {index < steps.length - 1 && (
                <div className="absolute left-5 top-10 w-0.5 h-[calc(100%-0.5rem)] bg-gray-200 dark:bg-slate-700" />
              )}

              <div className="relative z-[1] flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
                  {step.number}
                </span>
              </div>

              <div
                className={index < steps.length - 1 ? "pb-6 flex-1" : "flex-1"}
              >
                <div className="flex items-center gap-2 mb-1">
                  <step.icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {step.title}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {step.description}
                </p>
                {step.action && step.actionHref && (
                  <a
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                    href={step.actionHref}
                  >
                    {step.action}
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-1">
          <Wrench className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Trade Configuration
          </h2>
          {quickBooksTrade && (
            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold rounded">
              CONFIGURED
            </span>
          )}
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Select the trade for this company&apos;s QuickBooks Online
          integration. This must be configured before data import can begin.
        </p>

        {quickBooksTrade ? (
          <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-900 dark:text-green-200">
                Selected trade:{" "}
                <span className="font-bold">{quickBooksTrade.tradeName}</span>
              </p>
              <p className="text-sm text-green-800 dark:text-green-300 mt-1">
                All invoices imported from QuickBooks Online will be assigned to
                this trade.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Trade <span className="text-red-500">*</span>
                </label>
                <Select
                  onValueChange={setSelectedTradeId}
                  value={selectedTradeId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a trade..." />
                  </SelectTrigger>
                  <SelectContent>
                    {trades.map((trade) => (
                      <SelectItem key={trade.id} value={trade.id.toString()}>
                        {trade.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button
                className="h-9 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2 transition-colors"
                disabled={!selectedTradeId || isSavingTrade}
                onClick={handleSaveTrade}
              >
                <Save className="w-4 h-4" />
                {isSavingTrade ? "Saving..." : "Save Trade"}
              </button>
            </div>
            {tradeError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 text-sm text-red-700 dark:text-red-400">
                {tradeError}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Club Membership SKU Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Define the SKUs that identify club memberships in your QuickBooks
          invoices. These SKUs will be used to automatically detect and tag club
          members.
        </p>
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {activeCount} Active SKUs
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {totalCount} Total SKUs
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
          Add New Membership SKU
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              SKU Code <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-slate-700 rounded-md text-sm bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onChange={(e) => setNewSkuPattern(e.target.value)}
              placeholder="e.g., CLUB-ANNUAL"
              type="text"
              value={newSkuPattern}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Membership Type
            </label>
            <select
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-slate-700 rounded-md text-sm bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onChange={(e) =>
                setMembershipType(e.target.value as "annual" | "monthly")
              }
              value={membershipType}
            >
              <option value="annual">Annual (12 months)</option>
              <option value="monthly">Monthly (30 days)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <input
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-slate-700 rounded-md text-sm bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              type="text"
              value={description}
            />
          </div>

          {validationError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 text-sm text-red-700 dark:text-red-400">
              {validationError}
            </div>
          )}

          <div className="pt-2">
            <button
              className="px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2 transition-colors"
              disabled={isCreating}
              onClick={handleAddSku}
            >
              <Plus className="w-4 h-4" />
              {isCreating ? "Adding..." : "Add SKU"}
            </button>
          </div>
        </div>
      </div>

      {/* Configured SKUs Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
        <div className="p-6 border-b border-gray-200 dark:border-slate-700">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Configured SKUs
            </h3>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  className="pl-9 pr-4 py-2 border border-gray-300 dark:border-slate-700 rounded-md text-sm bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search SKUs..."
                  type="text"
                  value={searchTerm}
                />
              </div>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2 transition-colors"
                disabled={!hasChanges || isSaving}
                onClick={handleSaveAllChanges}
              >
                <Save className="w-4 h-4" />
                {isSaving ? "Saving..." : "Save All Changes"}
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Loading SKUs...
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredSkus.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {searchTerm
              ? "No SKUs found matching your search"
              : "No membership SKU patterns configured yet."}
          </div>
        )}

        {/* SKU Table */}
        {!loading && filteredSkus.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700">
                <tr>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-700 dark:text-gray-200">
                    Status
                  </th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-700 dark:text-gray-200">
                    SKU Code
                  </th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-700 dark:text-gray-200">
                    Type
                  </th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-700 dark:text-gray-200">
                    Description
                  </th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-700 dark:text-gray-200">
                    Validity Period
                  </th>
                  <th className="text-right py-3 px-6 text-sm font-medium text-gray-700 dark:text-gray-200">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {filteredSkus.map((sku) => (
                  <tr
                    className="hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    key={sku.uuid}
                  >
                    <td className="py-4 px-6">
                      <button
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          sku.isActive
                            ? "bg-green-500"
                            : "bg-gray-300 dark:bg-slate-600"
                        }`}
                        onClick={() => handleToggleStatus(sku.uuid)}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            sku.isActive ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>
                    <td className="py-4 px-6">
                      <code className="text-sm font-mono text-gray-900 dark:text-gray-100">
                        {sku.skuPattern}
                      </code>
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          sku.membershipDuration === "annual"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                            : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                        }`}
                      >
                        {getMembershipTypeLabel(sku.membershipDuration)}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                      {sku.description || "-"}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                      {getValidityPeriod(sku.membershipDuration)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex justify-end gap-2">
                        <button
                          className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30 rounded transition-colors"
                          onClick={() => handleEdit(sku)}
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30 rounded transition-colors"
                          onClick={() => handleDelete(sku)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info Box */}
        <div className="m-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
              How it works:
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-300">
              When invoices are synced from QuickBooks, the system will check
              each line item against these SKUs. If a match is found, the
              customer will be automatically tagged as a club member based on
              the membership type (annual = 12 months validity, monthly = 30
              days validity from invoice date).
            </p>
          </div>
        </div>
      </div>

      <EditMembershipSkuDrawer
        currentTenant={currentTenant}
        onClose={() => {
          setIsEditOpen(false);
          dispatch(fetchMembershipSkus(currentTenant));
        }}
        open={isEditOpen}
      />
      <DeleteConfirmDialog
        description={
          skuToDelete
            ? `Are you sure you want to delete the SKU pattern "${skuToDelete.skuPattern}"? This action cannot be undone.`
            : ""
        }
        onClose={() => setSkuToDelete(null)}
        onConfirm={confirmDelete}
        open={!!skuToDelete}
        title="Delete Membership SKU Pattern?"
      />
    </div>
  );
};
